import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEventHandler,
} from 'react';
import classNames from 'classnames';
import { toZonedTime, format } from 'date-fns-tz';
import { first, isNil, last, slice } from 'lodash';
import { useWindowScroll } from 'react-use';
import { Avatar, Box, Card, Chip, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { getUserSettings, getWindow, trackEvent } from '@surfline/web-common';

import { MDY } from 'common/constants';
import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import ForecasterProfile from 'components/ForecasterProfile';
import StarCircle from 'components/Icons/StarCircle';
import SubregionForecastCard from 'components/SubregionForecastCard';
import ForecastDayContent from 'components/ForecastDayContent';
import config from 'config';
import useCustomError from 'hooks/useCustomError';
import {
  useMaxWidthTabletLarge,
  useMaxWidthDesktopSmall,
  useMaxWidthTablet,
} from 'hooks/useMediaQueries';
import { getSubregionOverviewData } from 'selectors/subregion';
import { useAppSelector } from 'stores/hooks';
import { getSpotReportNotes } from 'selectors/spot';
import type { GraphDataError } from 'types/graphs';
import type { Condition, SubregionOverviewData } from 'types/subregion';
import type { SwellsTimestep } from 'types/swell';
import type { SpotReportNotes } from 'types/spot';
import {
  convertToMilliseconds,
  convertUtcOffsetToMilliseconds,
  formatTimestampInTimezone,
} from 'utils/time';

import styles from './ForecastSummary.module.scss';

const upgradeUrl = `${config.surflineHost}${config.funnelUrl}`;

type ForecastSummaryMode = 'graph' | 'table' | 'graph-custom';

export interface ForecastSummaryProps {
  days: Condition[];
  daysToDisplay: number[];
  error?: GraphDataError;
  graphHeight?: number;
  graphTop?: number;
  graphWidth?: number;
  historicDates?: Array<SwellsTimestep[]>;
  isHistoricView: boolean;
  lastPublished?: number;
  loading: boolean;
  mode?: ForecastSummaryMode;
  spotId?: string;
  spotName?: string;
  timezone?: string;
}

interface ForecastSummaryCardProps {
  condition: Condition;
  dateFormat?: string;
  dayLabelsHeaderSticky: boolean;
  forecastNote?: string | null;
  historicDate: SwellsTimestep;
  index: number;
  isHistoricView: boolean;
  isTabletView: boolean;
  isToday?: boolean;
  isTomorrow?: boolean;
  lastPublished?: number;
  loading: boolean;
  mode: ForecastSummaryMode;
  spotId?: string;
  spotName?: string;
  subregionId?: string;
  subregionName?: string;
  timezone?: string;
}

interface ForecastSummaryBodyProps {
  condition: Condition;
  isTabletView: boolean;
  loading: boolean;
  mode: ForecastSummaryMode;
}

const formatHeadline = ({
  condition,
  index = 0,
  isHistoricView,
  dateFormat,
}: {
  isHistoricView: boolean;
  condition: Condition;
  index: number;
  dateFormat?: string;
}) => {
  const tabletViewDateFormat = dateFormat === MDY ? 'M/d' : 'd/M';
  const desktopDateFormat = dateFormat === MDY ? 'EEEE, M/d' : 'EEEE, d/M';
  // using the default UTC with no offset because the simple dates, forecastDay,
  // are returned from the API with the correct day
  const timeZone = '+00:00';
  const utcDate = condition?.forecastDay
    ? toZonedTime(new Date(condition?.forecastDay), timeZone)
    : new Date(condition.timestamp * 1000);

  if (!condition?.forecastDay && !condition?.timestamp) {
    return '';
  }

  if (index === 0 && !isHistoricView) {
    return `Today, ${format(utcDate, tabletViewDateFormat)}`;
  }
  if (index === 1 && !isHistoricView) {
    return `Tomorrow, ${format(utcDate, tabletViewDateFormat)}`;
  }

  return format(utcDate, desktopDateFormat);
};

const ForecastSummaryBody: React.FC<ForecastSummaryBodyProps> = ({
  condition,
  isTabletView,
  loading,
  mode,
}) => {
  const lastUpdate = {
    timestamp: condition?.timestamp,
    utcOffset: condition?.utcOffset,
  };

  return (
    <Box>
      {(!isTabletView && mode !== 'table') || mode === 'table' ? (
        <Box className={styles.forecastSummaryBody}>
          <Box className={styles.forecasterProfile}>
            {loading ? (
              <Box className={styles.skeleton}>
                <Skeleton variant="circular" animation="wave">
                  <Avatar />
                </Skeleton>
              </Box>
            ) : (
              <ForecasterProfile
                conditions=""
                iconUrl={condition?.forecaster?.avatar ?? ''}
                lastUpdate={isTabletView ? lastUpdate : null}
                name={condition?.forecaster?.name ?? ''}
                alt={condition?.forecaster?.title}
                isShowConditionsBorder={false}
                isShowName={false}
                useLink={false}
              />
            )}
          </Box>
        </Box>
      ) : (
        <Stack direction="row" spacing={0}>
          {loading ? (
            <>
              <Box className={styles.skeleton}>
                <Skeleton variant="circular" animation="wave">
                  <Avatar />
                </Skeleton>
              </Box>
              <Skeleton variant="rectangular" animation="wave" height={169} width="100%" />
            </>
          ) : (
            <ForecasterProfile
              conditions=""
              iconUrl={condition?.forecaster?.avatar ?? ''}
              lastUpdate={isTabletView ? lastUpdate : null}
              name={condition?.forecaster?.name ?? ''}
              alt={condition?.forecaster?.title}
              isShowConditionsBorder={false}
              isShowName={false}
              useLink={false}
            />
          )}
        </Stack>
      )}
    </Box>
  );
};

const ForecastSummaryCard = ({
  condition,
  dateFormat,
  dayLabelsHeaderSticky,
  forecastNote,
  historicDate,
  index,
  isHistoricView,
  isTabletView,
  isToday = false,
  isTomorrow = false,
  lastPublished,
  loading,
  mode,
  spotId,
  spotName,
  subregionId,
  subregionName,
  timezone,
}: ForecastSummaryCardProps) => {
  const isMaxWidthTablet = useMaxWidthTablet();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipContainerRef = useRef<HTMLDivElement>(null);

  const formattedHistoricDate = useMemo(
    () =>
      formatTimestampInTimezone(
        historicDate?.timestamp || 0,
        historicDate?.utcOffset || 0,
        'yyyy-MM-dd',
      ),
    [historicDate?.timestamp, historicDate?.utcOffset],
  );

  const summaryCondition = useMemo(
    () => ({
      ...condition,
      forecastDay:
        isHistoricView && formattedHistoricDate ? formattedHistoricDate : condition.forecastDay,
    }),
    [condition, formattedHistoricDate, isHistoricView],
  );

  const headline = useMemo(
    () =>
      formatHeadline({
        isHistoricView,
        condition: summaryCondition,
        index,
        dateFormat,
      }),
    [dateFormat, index, isHistoricView, summaryCondition],
  );

  const shouldShowTooltip = useMemo(
    () => !!condition?.headline && !!condition?.observation,
    [condition?.headline, condition?.observation],
  );

  const toggleTooltip: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      if (!shouldShowTooltip) {
        trackEvent('Clicked Subscribe CTA', {
          spotId,
          location: `forecast content graph`,
          spotName,
          pageName: 'Spot Report',
          contentType: `Day ${index}`,
        });
        getWindow()?.location.assign(upgradeUrl);
      }
      if (
        tooltipContainerRef.current &&
        !tooltipContainerRef.current.contains(event.target as Node)
      ) {
        if (!isTooltipOpen && shouldShowTooltip) {
          trackEvent('Button Clicked', {
            buttonText: 'Read More',
            uiElement: 'Forecaster headline',
            dayIndex: index + 1,
          });
        }
        setIsTooltipOpen((prev) => !prev);
      }
    },
    [index, isTooltipOpen, shouldShowTooltip, spotId, spotName],
  );

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
      setIsTooltipOpen(false);
    }
  }, []);

  const title = useMemo(() => {
    if (isToday) return 'Today';
    if (isTomorrow) return 'Tomorrow';
    if (condition?.dayToWatch) return 'Day to Watch';
    return undefined;
  }, [condition?.dayToWatch, isToday, isTomorrow]);

  const forecaster = useMemo(() => {
    if (condition?.forecaster) {
      return {
        name: condition.forecaster.name,
        avatarUrl: condition.forecaster.avatar,
      };
    }
    return null;
  }, [condition.forecaster]);

  const tooltipDate = useMemo(() => {
    if (isNil(condition.timestamp) || isNil(condition.utcOffset)) return undefined;
    const timestamp = convertToMilliseconds(condition.timestamp);
    const utcOffset = convertUtcOffsetToMilliseconds(condition.utcOffset);
    const adjustedTimestamp = timestamp + utcOffset;
    return new Date(adjustedTimestamp);
  }, [condition.timestamp, condition.utcOffset]);

  const showPaywall = false;

  useEffect(() => {
    if (isTooltipOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTooltipOpen, handleClickOutside]);

  if (
    !isHistoricView &&
    condition?.forecaster?.name &&
    spotId &&
    spotName &&
    subregionId &&
    subregionName
  ) {
    if (isMaxWidthTablet) {
      if (!condition.dayToWatch && !isToday && !isTomorrow) {
        return <div className="first-div" style={{ paddingTop: '50px' }} />;
      }

      return (
        <Card
          variant="none"
          className={classNames(styles.forecastSummaryCard, {
            [styles.stickyHeader]: dayLabelsHeaderSticky,
          })}
        >
          {mode !== 'table' &&
            (loading ? (
              <Skeleton variant="rectangular" animation="wave" width="50%" />
            ) : (
              <Typography variant="callout1" component="p" data-testid="forecast-summary-headline">
                {headline}
              </Typography>
            ))}
          <Box pt={2}>
            <ForecastDayContent
              conditions=""
              dayIndex={index}
              forecastDate={condition.forecastDay}
              forecastNote={forecastNote}
              forecaster={forecaster}
              headline={condition.headline as string}
              isPaywallActive={showPaywall}
              lastPublished={lastPublished}
              observation={condition.observation as string}
              spotId={spotId}
              spotName={spotName}
              subregionId={subregionId}
              subregionName={subregionName}
              title={title}
              titleSuffix={
                condition.dayToWatch ? (
                  <StarCircle className={styles.star} data-testid="forecast-summary-star" />
                ) : undefined
              }
              type="graph"
              utcOffset={condition.utcOffset as number}
            />
          </Box>
        </Card>
      );
    }

    // Show full summary if it's one of the first 5 days, OR if it's a day to watch for days after 5
    const isFirstFiveDays = index < 5;
    const isDayToWatchAfterFive = index >= 5 && condition.dayToWatch;
    const shouldShowFullSummary = isFirstFiveDays || isDayToWatchAfterFive || isToday || isTomorrow;

    if (!shouldShowFullSummary) {
      return (
        <Card
          variant="none"
          className={classNames(styles.forecastSummaryCard, {
            [styles.stickyHeader]: dayLabelsHeaderSticky,
            [styles.inline]: true,
          })}
        >
          {mode !== 'table' &&
            (loading ? (
              <Skeleton variant="rectangular" animation="wave" width="50%" />
            ) : (
              <Typography
                variant="callout1"
                component="p"
                data-testid="forecast-summary-headline"
                className={styles.headline}
              >
                {headline}
              </Typography>
            ))}
        </Card>
      );
    }

    return (
      <button
        className={styles.forecastButton}
        data-testid="forecast-summary-button"
        onClick={toggleTooltip}
        ref={buttonRef}
        type="button"
      >
        <div
          ref={tooltipContainerRef}
          id="tooltip-container"
          data-testid="forecast-summary-tooltip-container"
        />
        <Tooltip
          data-testid="forecast-summary-tooltip"
          title={
            <SubregionForecastCard
              className={styles.forecastCard}
              contentBlock={`Day ${index}`}
              contentBody={condition.observation}
              contentHeadline={condition.headline}
              date={tooltipDate}
              forecasterName={condition.forecaster?.name}
              isFeedbackActive
              pageName="Spot Report"
              showModalLink
              subregionId={subregionId}
              subregionName={subregionName}
              timezone={timezone}
              type="highlights"
            >
              {condition.headline && (
                <Typography variant="callout1" className={styles.tooltipHeadline}>
                  {condition.headline}
                </Typography>
              )}
              {condition.observation && (
                <Typography component="p" variant="footnote" className={styles.tooltipObservation}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: condition.observation,
                    }}
                  />
                </Typography>
              )}
            </SubregionForecastCard>
          }
          open={isTooltipOpen && shouldShowTooltip}
          slotProps={{
            popper: {
              className: styles.tooltipPopper,
              container: tooltipContainerRef.current,
            },
          }}
        >
          <Card
            variant="none"
            className={classNames(styles.forecastSummaryCard, {
              [styles.stickyHeader]: dayLabelsHeaderSticky,
              [styles.inline]: true,
            })}
          >
            {mode !== 'table' &&
              (loading ? (
                <Skeleton variant="rectangular" animation="wave" width="50%" />
              ) : (
                <Typography
                  variant="callout1"
                  component="p"
                  data-testid="forecast-summary-headline"
                  className={styles.headline}
                >
                  {headline}
                </Typography>
              ))}
            {condition.dayToWatch && (
              <StarCircle className={styles.star} data-testid="forecast-summary-star" />
            )}
            {condition?.human && !dayLabelsHeaderSticky && !isHistoricView && (
              <Box ml={1}>
                <ForecastSummaryBody
                  condition={condition}
                  isTabletView={isTabletView}
                  loading={loading}
                  mode={mode}
                />
              </Box>
            )}
            {mode !== 'table' && condition?.headline && (
              <Box className={styles.readMore}>
                <Typography variant="body">
                  {condition?.headline && (
                    <span className={styles.readMoreHeadline}>{condition.headline}</span>
                  )}
                  <Chip component="span" label="Read More" className={styles.readMoreChip} />
                </Typography>
              </Box>
            )}
          </Card>
        </Tooltip>
      </button>
    );
  }

  return (
    <Card
      variant="none"
      className={classNames(styles.forecastSummaryCard, {
        [styles.stickyHeader]: dayLabelsHeaderSticky,
      })}
    >
      {mode !== 'table' &&
        (loading ? (
          <Skeleton variant="rectangular" animation="wave" width="50%" />
        ) : (
          <Typography variant="callout1" component="p" data-testid="forecast-summary-headline">
            {headline}
          </Typography>
        ))}
      {condition?.human && !dayLabelsHeaderSticky && !isHistoricView && (
        <ForecastSummaryBody
          condition={condition}
          isTabletView={isTabletView}
          loading={loading}
          mode={mode}
        />
      )}
    </Card>
  );
};

export const ForecastSummary: React.FC<ForecastSummaryProps> = ({
  days,
  daysToDisplay,
  error,
  graphHeight,
  graphTop,
  graphWidth,
  historicDates,
  isHistoricView,
  lastPublished,
  loading,
  mode = 'graph',
  spotId,
  spotName,
  timezone,
}) => {
  const isContainerFullBleed = useMaxWidthTabletLarge();
  const isTabletView = useMaxWidthDesktopSmall();
  const settings = useAppSelector(getUserSettings);
  const subregionOverviewData: SubregionOverviewData = useAppSelector(getSubregionOverviewData);
  const { errorMessage, ErrorIcon, hasError } = useCustomError({ error });
  const spotReportNotes: SpotReportNotes = useAppSelector(getSpotReportNotes);

  const dateFormat = settings?.date?.format;
  const windowScroll = useWindowScroll();
  const dayLabelsHeader = useRef<HTMLDivElement>(null);
  const dayLabelsHeaderContainer = useRef<HTMLDivElement>(null);
  const dayLabelsHeaderTop = dayLabelsHeader?.current?.getBoundingClientRect()?.top ?? 0;

  const dayLabelsHeaderOffset = useMemo(() => {
    const bodyRect = getWindow()?.document?.body?.getBoundingClientRect();
    const offset = dayLabelsHeaderTop - (bodyRect?.top ?? 0) + 3;
    return offset;
  }, [dayLabelsHeaderTop]);

  const dayLabelsScrollEnd = useMemo(
    () => (graphHeight ?? 0) + (graphTop ?? 0),
    [graphHeight, graphTop],
  );

  const dayLabelsHeaderSticky = useMemo(
    () =>
      dayLabelsHeaderOffset === null
        ? false
        : windowScroll?.y > dayLabelsHeaderOffset && windowScroll?.y < dayLabelsScrollEnd,
    [dayLabelsHeaderOffset, dayLabelsScrollEnd, windowScroll?.y],
  );

  const conditions = useMemo(() => {
    const lastDayToDisplay = (last(daysToDisplay) ?? 0) + 1;
    return slice(days, first(daysToDisplay), lastDayToDisplay);
  }, [days, daysToDisplay]);

  const displayDates = useMemo(() => {
    const lastDayToDisplay = (last(daysToDisplay) ?? 0) + 1;
    return slice(historicDates, first(daysToDisplay), lastDayToDisplay);
  }, [daysToDisplay, historicDates]);

  const containerWidth = useMemo(
    () =>
      dayLabelsHeaderContainer?.current?.offsetWidth
        ? dayLabelsHeaderContainer?.current?.offsetWidth
        : (graphWidth ?? 0),
    [graphWidth],
  );

  const dayContainerWidth = useMemo(
    () => (containerWidth > 0 ? containerWidth / daysToDisplay.length : 'inherit'),
    [containerWidth, daysToDisplay.length],
  );

  const forecastNote = useMemo(() => {
    if (spotReportNotes) {
      const { subregion: subregionNote, spot: spotNote } = spotReportNotes;
      if (subregionNote || spotNote) {
        return `${subregionNote || ''}${subregionNote && spotNote ? '<br /><br />' : ''}${
          spotNote || ''
        }`;
      }
    }
    return undefined;
  }, [spotReportNotes]);

  const forecastSummaryCards = useMemo(
    () =>
      conditions.map((condition, index) => {
        const isToday = index === 0 && daysToDisplay?.[index] === 0;
        const isTomorrow = (index === 0 || index === 1) && daysToDisplay?.[index] === 1;
        return (
          <ErrorBoundary
            key={condition.timestamp}
            error={hasError}
            errorBoundaryId="forecast-summary"
            render={() => (
              <Box data-testid="forecast-summary-error">
                <ErrorMessage Icon={ErrorIcon} message={errorMessage} />
              </Box>
            )}
          >
            <Box
              className={styles.forecastSummaryItem}
              data-testid="forecast-summary-day-label"
              key={condition.timestamp}
              maxWidth={dayContainerWidth}
            >
              <ForecastSummaryCard
                condition={condition}
                dateFormat={dateFormat}
                dayLabelsHeaderSticky={false}
                forecastNote={forecastNote}
                historicDate={displayDates?.[index]?.[0]}
                index={daysToDisplay[index]}
                isHistoricView={isHistoricView}
                isTabletView={isTabletView}
                isToday={isToday}
                isTomorrow={isTomorrow}
                key={condition.timestamp}
                lastPublished={lastPublished}
                loading={loading}
                mode={mode}
                spotId={spotId}
                spotName={spotName}
                subregionId={subregionOverviewData?._id}
                subregionName={subregionOverviewData?.name}
                timezone={timezone}
              />
            </Box>
          </ErrorBoundary>
        );
      }),
    [
      ErrorIcon,
      conditions,
      dateFormat,
      dayContainerWidth,
      daysToDisplay,
      displayDates,
      errorMessage,
      forecastNote,
      hasError,
      isHistoricView,
      isTabletView,
      lastPublished,
      loading,
      mode,
      spotId,
      spotName,
      subregionOverviewData,
      timezone,
    ],
  );

  const forecastSummaryCardsSticky = useMemo(
    () =>
      conditions.map((condition, index) => {
        const isToday = index === 0 && daysToDisplay?.[index] === 0;
        const isTomorrow = (index === 0 || index === 1) && daysToDisplay?.[index] === 1;

        return (
          <ErrorBoundary
            key={condition.timestamp}
            error={hasError}
            errorBoundaryId="forecast-summary-sticky"
            render={() => (
              <Box data-testid="forecast-summary-error">
                <ErrorMessage Icon={ErrorIcon} message={errorMessage} />
              </Box>
            )}
          >
            <Box
              className={styles.forecastSummaryItem}
              key={condition.timestamp}
              maxWidth={dayContainerWidth}
            >
              <ForecastSummaryCard
                condition={condition}
                dateFormat={dateFormat}
                dayLabelsHeaderSticky={dayLabelsHeaderSticky}
                historicDate={displayDates?.[index]?.[0]}
                index={daysToDisplay[index]}
                isHistoricView={isHistoricView}
                isTabletView={isTabletView}
                isToday={isToday}
                isTomorrow={isTomorrow}
                key={condition.timestamp}
                lastPublished={lastPublished}
                loading={loading}
                mode={mode}
                spotId={spotId}
                timezone={timezone}
              />
            </Box>
          </ErrorBoundary>
        );
      }),
    [
      ErrorIcon,
      conditions,
      dateFormat,
      dayContainerWidth,
      dayLabelsHeaderSticky,
      daysToDisplay,
      displayDates,
      errorMessage,
      hasError,
      isHistoricView,
      isTabletView,
      lastPublished,
      loading,
      mode,
      spotId,
      timezone,
    ],
  );

  if (loading) {
    return (
      <Box
        className={classNames({
          [styles.forecastSummary]: true,
          [styles.forecastSummaryGraphCustom]: mode === 'graph-custom',
          'sl-section-full-bleed': dayLabelsHeaderSticky || isContainerFullBleed,
        })}
        data-testid="forecast-summary-loading"
      />
    );
  }

  if (!days?.length) return null;

  return (
    <Box
      className={classNames({
        [styles.forecastSummary]: true,
        [styles.forecastSummaryGraphCustom]: mode === 'graph-custom',
        'sl-section-full-bleed': dayLabelsHeaderSticky || isContainerFullBleed,
      })}
      data-testid="forecast-summary"
      ref={dayLabelsHeaderContainer}
    >
      <Stack
        alignContent="flex-start"
        alignItems="flex-start"
        direction="row"
        ref={dayLabelsHeader}
      >
        {forecastSummaryCards}
      </Stack>
      {dayLabelsHeaderSticky ? (
        <Stack
          direction="row"
          alignItems="flex-start"
          alignContent="flex-start"
          bgcolor="white"
          position="fixed"
          top={0}
          zIndex={10}
          width={isContainerFullBleed ? '100%' : containerWidth || 'inherit'}
          left={isContainerFullBleed ? 0 : 'unset'}
          maxHeight="50px"
        >
          {forecastSummaryCardsSticky}
        </Stack>
      ) : null}
    </Box>
  );
};

export default React.memo(ForecastSummary);
